import {
  CustomLabel,
  DocumentCategory,
  DOCUMENT_CATEGORY_TABLE_NAME,
  DocumentCategoryWithoutReadIdAndClientId,
  EntityType,
  FileCategory,
  LAST_GRANTED_OWNER_FILE_CATEGORY,
  Model,
  OnCreateDocumentCategorySubscription,
  OnDeleteDocumentCategorySubscription,
  OnUpdateDocumentCategorySubscription,
  cleanInputUpdate,
  createDocumentCategory as createMutation,
  CreateDocumentCategoryMutationVariables as createMutationVariables,
  deleteDocumentCategory as deleteMutation,
  getDefaultDocumentCategory,
  getDocumentCategory,
  getReadId,
  getTableClientId,
  onCreateDocumentCategory,
  onDeleteDocumentCategory,
  onUpdateDocumentCategory,
  syncDocumentCategories,
  updateDocumentCategory as updateMutation,
} from '@rentguru/commons-utils';
import {
  MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH,
  deleteAndHideEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  useSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { waitForNextAccessProbe, waitForReadAccess } from 'src/utils/accessSyncUtils';

import { FolderDatabaseAction } from './Folderhook/folderContextUtils';
import { useFolderDatabase } from './Folderhook/FolderDatabaseContext';
import { useUser } from './UserContext';

const MAX_DOCUMENT_CATEGORY_FETCH_ATTEMPTS = 5;

const ENTITY_MODEL_NAME: Model = 'DocumentCategory';

export interface FileCategoryContext {
  fileCategories: DocumentCategoryWithoutReadIdAndClientId[];
  loading: boolean;
  fetchDocumentCategories: () => Promise<DocumentCategoryWithoutReadIdAndClientId[]>;
  createFileCategory: (
    fileCategory: CustomLabel[],
    entity: EntityType,
    parentId?: string
  ) => Promise<DocumentCategoryWithoutReadIdAndClientId>;
  updateFileCategory: (
    original: DocumentCategoryWithoutReadIdAndClientId,
    updates: Partial<DocumentCategoryWithoutReadIdAndClientId>
  ) => Promise<DocumentCategoryWithoutReadIdAndClientId>;
  deleteFileCategory: (
    fileCategory: DocumentCategoryWithoutReadIdAndClientId
  ) => Promise<DocumentCategoryWithoutReadIdAndClientId>;
  deleteMultipleFilesCategoriesAtOnce: (
    fileCategoriesToDelete: DocumentCategoryWithoutReadIdAndClientId[]
  ) => Promise<void>;
  getDocumentCategoryById: (id: string) => DocumentCategoryWithoutReadIdAndClientId;
  getDocumentCategoryByFileCategory: (
    fileCategory: FileCategory
  ) => DocumentCategoryWithoutReadIdAndClientId | undefined;
  getOrCreateDocumentCategoryByFileCategory: (
    fileCategory: FileCategory
  ) => Promise<DocumentCategoryWithoutReadIdAndClientId>;
  isAwaitingOwnerCategoryGrants: boolean;
}

let inFlightDocumentCategories: {
  clientId: string;
  request: Promise<DocumentCategoryWithoutReadIdAndClientId[]>;
} | null = null;

const fetchDocumentCategory = async (clientId: string): Promise<DocumentCategoryWithoutReadIdAndClientId[]> => {
  if (inFlightDocumentCategories?.clientId === clientId) {
    return inFlightDocumentCategories.request;
  }
  const request: Promise<DocumentCategoryWithoutReadIdAndClientId[]> = list<DocumentCategoryWithoutReadIdAndClientId>(
    syncDocumentCategories,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME)
  )
    .then((fileCategories) => fileCategories ?? [])
    .finally(() => {
      if (inFlightDocumentCategories?.request === request) {
        inFlightDocumentCategories = null;
      }
    });
  inFlightDocumentCategories = { clientId, request };
  return request;
};

// eslint-disable-next-line no-redeclare
export const FileCategoryContext = createContext<FileCategoryContext | null>(null);

export const DocumentCategoryContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { clientId, isOwner } = useUser();
  const [fileCategories, setDocumentCategories] = useState<DocumentCategoryWithoutReadIdAndClientId[]>([]);
  const [loading, setIsLoading] = useState<boolean>(true);
  const [hasOwnerCategoryGrantWaitEnded, setHasOwnerCategoryGrantWaitEnded] = useState<boolean>(false);
  const ownerCategoryGrantsAbortControllerRef = useRef<AbortController | null>(null);
  const [fetchAttempt, setFetchAttempt] = useState<number>(1);
  const { addFolderToSync } = useFolderDatabase();

  useSubscriptions<
    OnCreateDocumentCategorySubscription,
    OnUpdateDocumentCategorySubscription,
    OnDeleteDocumentCategorySubscription
  >(
    onCreateDocumentCategory,
    onUpdateDocumentCategory,
    onDeleteDocumentCategory,
    async (data) => {
      const documentCategory = data.onCreateDocumentCategory as DocumentCategory;
      setDocumentCategories((oldDocumentCategories) => {
        if (oldDocumentCategories.some((category) => category.id === documentCategory.id)) {
          return oldDocumentCategories;
        }
        return [...oldDocumentCategories, documentCategory];
      });
    },
    async (data) => {
      const documentCategory = data.onUpdateDocumentCategory as DocumentCategory;
      await addFolderToSync(FolderDatabaseAction.UPDATE, undefined, documentCategory);
    },
    async (data) => {
      const documentCategory = data.onDeleteDocumentCategory as DocumentCategory;
      setDocumentCategories((oldDocumentCategories) =>
        oldDocumentCategories.filter((category) => category.id !== documentCategory.id)
      );
    }
  );

  useEffect(() => {
    if (isNil(clientId)) {
      setIsLoading(false);
      return undefined;
    }
    let unmounted = false;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    const fetch = async () => {
      try {
        const categories = await fetchDocumentCategory(clientId);
        if (!unmounted) {
          setDocumentCategories(categories);
        }
      } catch (error) {
        console.error('[fileCategory] fetching document categories failed', error);
        if (!unmounted && fetchAttempt < MAX_DOCUMENT_CATEGORY_FETCH_ATTEMPTS) {
          retryTimeout = setTimeout(
            () => setFetchAttempt((currentAttempt) => currentAttempt + 1),
            MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH
          );
        }
      } finally {
        if (!unmounted) {
          setIsLoading(false);
        }
      }
    };
    fetch();
    return () => {
      unmounted = true;
      clearTimeout(retryTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, fetchAttempt]);

  const fetchDocumentCategories = async (): Promise<DocumentCategoryWithoutReadIdAndClientId[]> => {
    if (!clientId) throw new Error('Client id is missing');
    setIsLoading(true);
    try {
      const categories = await fetchDocumentCategory(clientId);
      setDocumentCategories(categories);
      return categories;
    } finally {
      setIsLoading(false);
    }
  };

  const createFileCategory = async (
    labels: CustomLabel[],
    entity: EntityType,
    parentId?: string
  ): Promise<DocumentCategoryWithoutReadIdAndClientId> => {
    if (!clientId) throw new Error('Client id is missing');
    const newCategory = await mutation<DocumentCategory, createMutationVariables>(createMutation, {
      input: {
        labels,
        parentId,
        id: uuidv4(),
        fileCategory: FileCategory.CUSTOM,
        readId: getReadId(clientId, 'DocumentCategory'),
        clientId: getTableClientId(clientId, 'DocumentCategory'),
        entity,
      },
    });
    setDocumentCategories((prevDocumentCategories) => [...prevDocumentCategories, newCategory]);
    return newCategory;
  };

  const getOrCreateDocumentCategoryByFileCategory = async (
    fileCategory: FileCategory
  ): Promise<DocumentCategoryWithoutReadIdAndClientId> => {
    const existingCategory = fileCategories.find((category) => category.fileCategory === fileCategory);
    if (!isNil(existingCategory)) {
      return existingCategory;
    }
    if (!clientId) throw new Error('Client id is missing');
    const defaultCategory = getDefaultDocumentCategory(fileCategory);
    if (isNil(defaultCategory)) {
      throw new Error(`No default definition for the ${fileCategory} document category`);
    }
    const newCategory = await mutation<DocumentCategory, createMutationVariables>(createMutation, {
      input: {
        labels: defaultCategory.labels,
        id: uuidv4(),
        fileCategory,
        readId: getReadId(clientId, DOCUMENT_CATEGORY_TABLE_NAME),
        clientId: getTableClientId(clientId, DOCUMENT_CATEGORY_TABLE_NAME),
      },
    });
    setDocumentCategories((prevDocumentCategories) => [...prevDocumentCategories, newCategory]);
    return newCategory;
  };

  const updateFileCategory = async (
    original: DocumentCategoryWithoutReadIdAndClientId,
    updates: Partial<DocumentCategoryWithoutReadIdAndClientId>
  ): Promise<DocumentCategoryWithoutReadIdAndClientId> => {
    const updatedCategory = await mutation<DocumentCategory>(updateMutation, {
      input: {
        id: original.id,
        _version: original._version,
        ...cleanInputUpdate(updates, false),
      },
    });
    setDocumentCategories((documentCategoriesState) =>
      documentCategoriesState.map((category) => (category.id === updatedCategory.id ? updatedCategory : category))
    );
    return updatedCategory;
  };

  const deleteFileCategory = async (
    fileCategory: DocumentCategoryWithoutReadIdAndClientId
  ): Promise<DocumentCategoryWithoutReadIdAndClientId> => {
    const result = await deleteAndHideEntityWithFetchBefore<DocumentCategoryWithoutReadIdAndClientId>(
      fileCategory,
      getDocumentCategory,
      deleteMutation
    );
    if (result) {
      setDocumentCategories(fileCategories.filter((category) => category.id !== fileCategory.id));
    }

    return result;
  };

  const deleteMultipleFilesCategoriesAtOnce = async (
    fileCategoriesToDelete: DocumentCategoryWithoutReadIdAndClientId[]
  ) => {
    fileCategoriesToDelete.map((fileCategory) =>
      deleteAndHideEntityWithFetchBefore<DocumentCategoryWithoutReadIdAndClientId>(
        fileCategory,
        getDocumentCategory,
        deleteMutation
      )
    );
    const results = await Promise.all(fileCategoriesToDelete);
    if (results) {
      setDocumentCategories((prevCategories) =>
        prevCategories.filter((category) => !results.some((toDelete) => toDelete.id === category.id))
      );
    }
  };

  const getDocumentCategoryByFileCategory = (fileCategory: FileCategory) =>
    fileCategories.find((category) => category.fileCategory === fileCategory);

  const waitForDocumentCategoryByFileCategory = async (fileCategory: FileCategory, signal: AbortSignal) => {
    if (isNil(clientId)) {
      return undefined;
    }
    const fetchCategoriesForPolling = () =>
      fetchDocumentCategory(clientId).catch((error): DocumentCategoryWithoutReadIdAndClientId[] => {
        console.error('[fileCategory] polling document categories failed', error);
        return [];
      });
    const { granted, result } = await waitForReadAccess<DocumentCategoryWithoutReadIdAndClientId[]>(
      fetchCategoriesForPolling,
      (fetchedCategories) => fetchedCategories.some((category) => category.fileCategory === fileCategory),
      signal
    );
    // The list comes from an eventually consistent index: read it once more so earlier grants show up too.
    const settledCategories =
      granted && !signal.aborted ? await waitForNextAccessProbe().then(fetchCategoriesForPolling) : result;
    const categories = isEmpty(settledCategories) ? result : settledCategories;
    if (!isEmpty(categories)) {
      setDocumentCategories(categories);
    }
    return categories.find((category) => category.fileCategory === fileCategory);
  };

  const hasLastGrantedOwnerCategory = fileCategories.some(
    (category) => category.fileCategory === LAST_GRANTED_OWNER_FILE_CATEGORY
  );
  const isAwaitingOwnerCategoryGrants = isOwner && !hasLastGrantedOwnerCategory && !hasOwnerCategoryGrantWaitEnded;

  useEffect(() => () => ownerCategoryGrantsAbortControllerRef.current?.abort(), []);

  useEffect(() => {
    if (!isOwner || loading || hasLastGrantedOwnerCategory || !isNil(ownerCategoryGrantsAbortControllerRef.current)) {
      return;
    }
    // An existing owner's access sync can grant the technic categories after this session fetched them.
    const abortController = new AbortController();
    ownerCategoryGrantsAbortControllerRef.current = abortController;
    waitForDocumentCategoryByFileCategory(LAST_GRANTED_OWNER_FILE_CATEGORY, abortController.signal).finally(() => {
      if (!abortController.signal.aborted) {
        setHasOwnerCategoryGrantWaitEnded(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, loading, hasLastGrantedOwnerCategory]);

  const getDocumentCategoryById = (id: string): DocumentCategoryWithoutReadIdAndClientId => {
    return fileCategories.find((category) => category.id === id)!;
  };

  const values = useMemo(
    () => ({
      fileCategories,
      loading,
      fetchDocumentCategories,
      createFileCategory,
      deleteFileCategory,
      deleteMultipleFilesCategoriesAtOnce,
      getDocumentCategoryByFileCategory,
      getOrCreateDocumentCategoryByFileCategory,
      isAwaitingOwnerCategoryGrants,
      getDocumentCategoryById,
      updateFileCategory,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileCategories, loading, isAwaitingOwnerCategoryGrants]
  );

  return <FileCategoryContext.Provider value={values}>{children}</FileCategoryContext.Provider>;
};

export const useDocumentCategory = (): FileCategoryContext => {
  const context = useContext<FileCategoryContext | null>(FileCategoryContext);
  if (!context) {
    throw new Error('useDocumentCategory must be used within a DocumentCategoryProvider');
  }
  return context as FileCategoryContext;
};
